import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import ReportService from '../../services/ReportService';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { DebtorClientsDto, DebtorClientsRequest } from "@ontime/types";
import { DownloadExcel } from './ReportDialog';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { selectUserOrganizationsList } from '../../stateManagement/organizationUserSlice';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import { zodResolver } from '@hookform/resolvers/zod';
import InfoComponent from '../../customComponents/InfoComponent';

const DebtorClients: React.FC<{
    loading: boolean;
    setLoading: (input: boolean) => void;
}> = ({ loading, setLoading }) => {

    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<DebtorClientsDto[]>([]);

    const GetDebtorClients = async (filter: DebtorClientsRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetDebtorClients(filter);
            setData(response.data.result);
        } catch (error) {
            enqueueSnackbar('Failed to fetch place', { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const GetDebtorClientsExcel = async (filter: DebtorClientsRequest) => {
        setLoading(true);
        try {
            const response = await ReportService.GetDebtorClientsExcel(filter);
            DownloadExcel('DebtorClients', response.data);
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Failed to fetch Report', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const organizationList = useSelector(selectUserOrganizationsList);

    const AppointmentByClientIdSchema = z.object({
        organizationId: z.string(),
        creationTimeFrom: z.string().optional(),
        creationTimeTo: z.string().optional(),
    });

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<DebtorClientsRequest>({
        resolver: zodResolver(AppointmentByClientIdSchema),
        defaultValues: {
            organizationId: organizationList?.[0].organizationId ?? ''
        },
    });
    const search = (
        <Box gap={2} m={2}>
            <form onSubmit={handleSubmit(GetDebtorClients)} noValidate>
                <InfoComponent infoContent='Report filters'>
                    <Box display="flex" justifyContent="space-evenly" flexWrap='wrap' gap={2} mt={2}>
                        <FormControl sx={{ minWidth: '190px' }} error={!!errors.organizationId} disabled={loading}>
                            <InputLabel>Place</InputLabel>
                            <Controller
                                name="organizationId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Place"
                                        {...field}
                                    >
                                        {organizationList.map((item) => (
                                            <MenuItem key={item.organizationId} value={item.organizationId}>
                                                {item.organizationName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{errors.organizationId?.message}</FormHelperText>
                        </FormControl>

                        <Controller
                            name="creationTimeFrom"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="From"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                        <Controller
                            name="creationTimeTo"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    sx={{ width: '190px' }}
                                    label="To"
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : '')}
                                    disabled={loading}
                                    slotProps={{ field: { clearable: true } }}
                                />
                            )}
                        />
                    </Box>
                </InfoComponent>
                <CustomFormAction>
                    <CustomButton lable='Show Report' type='submit' disabled={loading} infoContent={`Click to show report`} />
                    <CustomButton lable='Download Excel' onClick={handleSubmit(GetDebtorClientsExcel)} disabled={loading} infoContent={`Click to download excel`} />
                </CustomFormAction>
            </form>
        </Box>
    )

    const debtorClientsType: GridColumnType<DebtorClientsDto> = {
        appointmentDate: { type: 'Text', header: 'Appointment Date' },
        appointmentTime: { type: 'Text', header: 'Appointment Time' },
        appointmentAmount: { type: 'Price', header: 'Appointment Amount' },
        totalPaid: { type: 'Price', header: 'Total Paid' },
        client: { type: 'Text', header: 'Client Name' },
        phoneNumber: { type: 'Text', header: 'Client Phone' },
    };
    return (
        <GridTable<DebtorClientsDto, DebtorClientsRequest, DebtorClientsDto>
            gridTitle='Debtor Clients Report'
            list={data}
            inputType={debtorClientsType}
            customSearch={search}
        />
    );
};

export default DebtorClients;
